import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Customer } from '../../types/customers';

export type { Customer };

export interface CustomersSliceState {
  items: Customer[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  hasNextPage: boolean;
  searchTerm: string;
  selectedCustomerId: string | null;
}

const initialState: CustomersSliceState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  pageSize: 50,
  hasNextPage: false,
  searchTerm: '',
  selectedCustomerId: null,
};

const computeHasNextPage = (page: number, pageSize: number, total: number) =>
  page * pageSize < total;

const customersSlice = createSlice({
  name: 'backofficeCustomers',
  initialState,
  reducers: {
    setCustomers: (
      state,
      action: PayloadAction<{
        items: Customer[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.items = action.payload.items;
      if (typeof action.payload.total === 'number') {
        state.total = action.payload.total;
      }
      if (typeof action.payload.page === 'number') {
        state.page = action.payload.page;
      }
      if (typeof action.payload.pageSize === 'number') {
        state.pageSize = action.payload.pageSize;
      }
      state.hasNextPage = computeHasNextPage(
        state.page,
        state.pageSize,
        state.total,
      );
      state.loading = false;
      state.error = null;
    },
    appendCustomers: (
      state,
      action: PayloadAction<{
        items: Customer[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      const incoming = action.payload.items || [];
      const seen = new Set(state.items.map(item => String(item._id)));
      const next = [...state.items];
      for (const item of incoming) {
        const id = String(item?._id || '');
        if (!id || seen.has(id)) continue;
        seen.add(id);
        next.push(item);
      }
      state.items = next;
      if (typeof action.payload.total === 'number') {
        state.total = action.payload.total;
      }
      if (typeof action.payload.page === 'number') {
        state.page = action.payload.page;
      }
      if (typeof action.payload.pageSize === 'number') {
        state.pageSize = action.payload.pageSize;
      }
      state.hasNextPage = computeHasNextPage(
        state.page,
        state.pageSize,
        state.total,
      );
      state.loading = false;
      state.error = null;
    },
    /** Page 1 fetched again on coming back to the list, so its figures are
     * current. With more pages loaded, those customers are swapped in place
     * and the rest kept, so the list doesn't shrink back to one page. */
    refreshCustomersFirstPage: (
      state,
      action: PayloadAction<{ items: Customer[]; total?: number }>,
    ) => {
      const fresh = action.payload.items || [];
      const freshIds = new Set(fresh.map(item => String(item._id)));
      const kept =
        state.page > 1
          ? state.items.filter(item => !freshIds.has(String(item._id)))
          : [];
      state.items = [...fresh, ...kept];
      if (typeof action.payload.total === 'number') {
        state.total = action.payload.total;
      }
      state.hasNextPage = computeHasNextPage(
        state.page,
        state.pageSize,
        state.total,
      );
      state.loading = false;
      state.error = null;
    },
    addCustomer: (state, action: PayloadAction<Customer>) => {
      const id = String(action.payload?._id || '');
      if (!id) return;
      state.items = [
        action.payload,
        ...state.items.filter(item => String(item._id) !== id),
      ];
    },
    /** Upsert: a customer fetched by id (deep link, list not loaded yet) must
     * still land in the store so the details screen can render from it. */
    updateCustomerInList: (state, action: PayloadAction<Customer>) => {
      const idx = state.items.findIndex(item => item._id === action.payload._id);
      if (idx !== -1) {
        state.items[idx] = { ...state.items[idx], ...action.payload };
      } else {
        state.items.push(action.payload);
      }
    },
    removeCustomer: (state, action: PayloadAction<string>) => {
      const deletedId = action.payload;
      const beforeLen = state.items.length;
      state.items = state.items.filter(item => item._id !== deletedId);
      if (state.items.length < beforeLen) {
        state.total = Math.max(0, state.total - 1);
      }
      if (state.selectedCustomerId === deletedId) {
        state.selectedCustomerId = null;
      }
      state.hasNextPage = computeHasNextPage(
        state.page,
        state.pageSize,
        state.total,
      );
    },
    setCustomersLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setCustomersError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCustomersPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number') {
        state.page = action.payload.page;
      }
      if (typeof action.payload.pageSize === 'number') {
        state.pageSize = action.payload.pageSize;
      }
      state.hasNextPage = computeHasNextPage(
        state.page,
        state.pageSize,
        state.total,
      );
    },
    setCustomersSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedCustomerId: (state, action: PayloadAction<string | null>) => {
      state.selectedCustomerId = action.payload;
    },
    clearCustomersState: () => initialState,
  },
});

export const {
  setCustomers,
  appendCustomers,
  refreshCustomersFirstPage,
  addCustomer,
  updateCustomerInList,
  removeCustomer,
  setCustomersLoading,
  setCustomersError,
  setCustomersPagination,
  setCustomersSearchTerm,
  setSelectedCustomerId,
  clearCustomersState,
} = customersSlice.actions;

export default customersSlice.reducer;
